/**
 * Figma Node IDs:
 * Design Frame: 1386:31323
 * Instance IDs: I945:24698, I1515:81987, I944:23233, I1515:81451, I944:23254, I944:23265, I945:23280
 * Composite: true
 * Last Updated: 2024-12-22
 * Generated by: Claude Code figma-component command
 */
import React, { useState } from "react";
import { CircleX, Hexagon } from "@labxchange/icons";
import { defineMessages, useIntl } from "react-intl";
import { RichText } from "@learningblocks/shared-components/lib/RichText";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { LxButton } from "../LxButton";
import { fetchTranslations } from "./locales/config";
import {
  feedbackBannerBase,
  feedbackBannerVariants,
  feedbackBannerIconContainer,
  feedbackBannerMessage,
} from "./FeedbackBanner.css";

export type FeedbackBannerVariant =
  | "success"
  | "fail"
  | "warning"
  | "information";

export interface FeedbackBannerProps {
  /**
   * The variant/type of feedback banner
   */
  variant: FeedbackBannerVariant;

  /**
   * The main message to display - can be a string or a message descriptor
   */
  message:
    | string
    | { id: string; defaultMessage?: string; description?: string };

  /**
   * Whether to show the close button
   * @default false
   */
  showCloseButton?: boolean;

  /**
   * Callback function called before the banner is destroyed
   */
  beforeClose?: () => void;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Test ID for testing purposes
   */
  testId?: string;

  /**
   * ID for the banner element (useful for aria-describedby references)
   */
  id?: string;
}

export const messages = defineMessages({
  closeBanner: {
    id: "FeedbackBanner.closeBanner",
    defaultMessage: "Close feedback banner",
    description: "Accessible label for close button",
  },
});

const FeedbackBannerInternal: React.FC<FeedbackBannerProps> = ({
  variant,
  message,
  showCloseButton = false,
  beforeClose,
  className,
  testId,
  id,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // If component is not visible, don't render anything (self-destruction)
  if (!isVisible) {
    return null;
  }

  const handleClose = () => {
    // Call beforeClose callback if provided
    if (beforeClose) {
      beforeClose();
    }
    // Destroy the component
    setIsVisible(false);
  };

  //Check if the string contains MathJax syntax (e.g., $$...$$ or $...$)
  const containsMathJax = (text: string | undefined): boolean => {
    if (!text || typeof text !== "string") return false;
    return /\$\$.*?\$\$|\$.*?\$/g.test(text);
  };

  const bannerClasses = [
    feedbackBannerBase,
    feedbackBannerVariants[variant],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const { formatMessage } = useIntl();

  return (
    <div
      className={bannerClasses}
      role="alert"
      data-testid={testId}
      data-variant={variant}
      id={id}
    >
      <div className={feedbackBannerIconContainer}>
        <Hexagon type={variant} />
      </div>
      <div className={feedbackBannerMessage}>
        <RichText
          value={message}
          addTagClasses={false}
          equation={containsMathJax(
            typeof message === "string" ? message : message.defaultMessage
          )}
        />
      </div>
      {showCloseButton && (
        <LxButton
          size="xs"
          shape="circle"
          variant="transparent"
          onClickHandler={handleClose}
          ariaLabel={formatMessage(messages.closeBanner)}
          Icon={CircleX}
        />
      )}
    </div>
  );
};

export const FeedbackBanner = (props: FeedbackBannerProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <FeedbackBannerInternal {...props} />
  </TranslationsProvider>
);
